import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import type { AutocompleteOption } from '@wartek-id/autocomplete'
import { Autocomplete } from '@wartek-id/autocomplete'
import { Button } from '@wartek-id/button-v1'
import { Checkbox, CheckboxGroup } from '@wartek-id/checkbox-v1'
import { DatePicker } from '@wartek-id/date-picker'
import { Icon } from '@wartek-id/icon-v2'
import { Link } from '@wartek-id/link'
import { Select } from '@wartek-id/select'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast'

import {
  useAPIGetSubjectByClassAndSchoolType,
  useAPIPelaksanaanDocumentPreparation,
  useAPIPelaksanaanProgress,
  useAPIPelaksanaanTargetPerilaku,
  useAPIUpsertPelaksanaanDocumentPreparation,
} from 'api/kinerja/useAPIKinerja'
import useSKPPelaksanaanTrackEvent from 'app/KinerjaPage/hooks/useSKPPelaksanaanTrackEvent'
import { SubmitConfirmationDialog } from 'app/KinerjaPage/pages/PelaksanaanV2/components/SubmitConfirmationDialog'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import { PENGELOLAAN_KINERJA_PELAKSANAAN_STEPS } from 'app/KinerjaShared/constants/pelaksanaanSteps'
import useMediaManager from 'app/KinerjaShared/hooks/useMediaManager'
import FileUpload from '../UploadBuktiDukung/components/FileUpload'
import { Header } from './components/Header'
import { IndicatorCard } from './components/IndicatorCard'
import { SaveToDraftDialog } from './components/SaveToDraftDialog'
import { TujuanDialog } from './components/TujuanDialog'
import { jenjangKelasMapping } from './constants/jenjangKelasMapping'
import useBeforePopState from './hooks/useBeforePopState'
import { generateErrorMessage, getErrorCode } from 'app/KinerjaShared/utils/api'
import { PANDUAN_PENGELOLAAN_GURU_UNGGAH_BUKTI_DUKUNG_URL } from 'app/KinerjaShared/utils/panduan'
import { useNewDocumentFlow } from 'app/KinerjaShared/hooks/useNewDocumentFlow'

import type { BuktiKaryaDocumentResponse } from 'types/bukti-karya'
import type { BuktiDukungFileInstance } from 'types/kinerja'
import type {
  IPelaksanaanDocumentPreparationForm,
  TStateDocumentPreparation,
} from 'types/kinerja-pelaksanaan'
import type { CustomError } from 'utils/customError'

interface ObservationStepProps {
  title: string
  description: string
  withIcon?: boolean
}

const MIN_YEAR = 2024

const ObservationStep = ({
  title,
  description,
  withIcon = false,
}: ObservationStepProps) => (
  <div className="flex flex-row gap-3">
    {withIcon && <Icon color="informational">arrow_circle_right</Icon>}
    <div className="w-60 max-w-full">
      <Text variant="action" className="!text-blue-60">
        {title}
      </Text>
      <Text variant="body">{description}</Text>
    </div>
  </div>
)

const SubHeader = ({ title }: { title: string }) => (
  <div className="p-6 bg-blue-5">
    <Text variant="heading-sm" as="h3">
      {title}
    </Text>
  </div>
)

const DokumenPersiapan = () => {
  const router = useRouter()
  const [dialogOpen, setDialogOpen] = useState<
    'tujuan' | 'save-draft' | 'submit'
  >(null)
  const [kelas, setKelas] = useState(null)
  const [jenjang, setJenjang] = useState(null)
  const [fase, setFase] = useState(null)
  const [fileModulAjar, setFileModulAjar] = useState<BuktiDukungFileInstance>({
    fileName: '',
    id: '',
    deskripsi: '',
    fileUrl: '',
  })
  const toast = useToast()
  const { trackEvent } = useSKPPelaksanaanTrackEvent()

  const { status: documentStatus } = useNewDocumentFlow()

  const {
    register,
    getValues,
    resetField,
    control,
    formState,
    handleSubmit,
    setValue,
    reset,
  } = useForm<IPelaksanaanDocumentPreparationForm>({
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      behavioralTargets: [],
      behavioralTargetEffort: '',
      observationDate: '',
      observationLevel: '',
      observationClass: '',
      observationSubject: '',
      observationNote: '',
      observationDocuments: [],
    },
  })

  const {
    data,
    isSuccess,
    isLoading: isLoadingPelaksanaanProgress,
  } = useAPIPelaksanaanProgress({
    opts: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const isEvaluated = data?.data?.evaluationDocumentState === 'READY'

  const {
    handleUpload,
    isLoading: isUploading,
    fileUploaded,
  } = useMediaManager({ uploadType: 'DOCUMENT_PREPARATION' })

  const CURRENT_SCREEN_NAME = 'form_a'

  useEffect(() => {
    trackEvent({
      activity_type: 'pelaksanaan_open_form_a',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: 'pelaksanaan_hub',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleBuktiKarya = (file: BuktiKaryaDocumentResponse) => {
    setFileModulAjar({
      fileName: file.title,
      fileUrl: file.fileUrl,
      deskripsi: 'BUKTI_KARYA',
      id: file.id,
    })
    setValue(
      'observationDocuments',
      [
        {
          fileName: file.title,
          fileUrl: file.fileUrl,
          type: 'BUKTI_KARYA',
        },
      ],
      {
        shouldDirty: true,
      }
    )
  }

  useEffect(() => {
    if (fileUploaded) {
      setFileModulAjar({
        fileName: fileUploaded.originalName,
        fileUrl: fileUploaded.url,
        deskripsi: 'MEDIA_MANAGER',
        id: fileUploaded.id,
      })
      setValue(
        'observationDocuments',
        [{ id: fileUploaded.id, type: 'MEDIA_MANAGER' }],
        {
          shouldDirty: true,
        }
      )
    }
  }, [fileUploaded, setValue])

  const { data: documentData, isSuccess: documentSuccess } =
    useAPIPelaksanaanDocumentPreparation({ opts: { useErrorBoundary: false } })

  const submitDocumentMutation = useAPIUpsertPelaksanaanDocumentPreparation()

  useEffect(() => {
    if (documentSuccess) {
      const {
        behavioralTargets,
        observationClass,
        observationLevel,
        observationSubject,
        observationDocuments,
        ...resetFormWithoutBehavioralTargets
      } = documentData.data.detail

      const validatedObservationLevel = !!jenjangKelasMapping[observationLevel]
        ? observationLevel
        : ''

      let validatedObservationClass = ''
      if (!!validatedObservationLevel) {
        validatedObservationClass = jenjangKelasMapping[
          validatedObservationLevel
        ].kelas.find((thisKelas) => thisKelas == observationClass)
      }

      const validatedObservationSubject =
        validatedObservationClass && validatedObservationLevel
          ? observationSubject
          : ''

      const parsedObservationDocuments = observationDocuments.map(
        (thisDocument) => {
          if (thisDocument.type === 'MEDIA_MANAGER') {
            return {
              id: thisDocument?.id,
              type: thisDocument?.type,
            }
          }
          return thisDocument
        }
      )

      const resetForm: IPelaksanaanDocumentPreparationForm = {
        ...resetFormWithoutBehavioralTargets,
        behavioralTargets: behavioralTargets.map(
          (thisBehavior) => thisBehavior.id
        ),
        observationLevel: validatedObservationLevel,
        observationClass: validatedObservationClass,
        observationSubject: validatedObservationSubject,
        observationDocuments: parsedObservationDocuments,
      }

      // Set observationDate value with validation if any
      if (!!documentData?.data?.detail?.observationDate) {
        setValue(
          'observationDate',
          documentData?.data?.detail?.observationDate,
          {
            shouldValidate: true,
          }
        )
      }

      reset(resetForm)

      setJenjang(resetForm.observationLevel)
      setKelas(resetForm.observationClass)
      setFase(jenjangKelasMapping[validatedObservationLevel]?.type)
      setFileModulAjar({
        fileName: observationDocuments[0]?.fileName,
        fileUrl: observationDocuments[0]?.fileUrl,
        id: observationDocuments[0]?.id,
        deskripsi: observationDocuments[0]?.type,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentData, documentSuccess, reset])

  const { data: behaviorData, isLoading: behaviorLoading } =
    useAPIPelaksanaanTargetPerilaku(data?.data?.contentId, {
      enabled: isSuccess,
      staleTime: 30 * 60 * 1000, // stale after 30 min, force to refetch upon mounting
    })

  const { data: mapelData, isSuccess: isSuccessMapel } =
    useAPIGetSubjectByClassAndSchoolType({
      kelas,
      fase,
      opts: {
        enabled: !!fase && !!kelas,
      },
    })

  const mapelList: AutocompleteOption<{
    value: string
    label: string
  }>[] = useMemo(() => {
    const injectList = { value: 'Lainnya', label: 'Lainnya' }
    if (isSuccessMapel) {
      return [
        ...mapelData?.data?.more_subject?.map((thisData) => {
          return { value: thisData.value, label: thisData.value }
        }),
        injectList,
      ]
    }
    return [injectList]
  }, [mapelData, isSuccessMapel])

  const handleSaveDocument = useCallback(
    (
      formData: IPelaksanaanDocumentPreparationForm,
      action: 'submit' | 'draft' | 'back'
    ) => {
      const saveState: TStateDocumentPreparation =
        action == 'submit' ? 'SUBMITTED' : 'DRAFT'

      submitDocumentMutation.mutate(
        { state: saveState, detail: formData },
        {
          onSuccess: () => {
            if (action != 'back') {
              setDialogOpen(null)
              let toastDialog = 'Berhasil kumpulkan dokumen!'
              if (action == 'draft') {
                toastDialog = 'Berhasil simpan draf!'
              }
              toast({
                type: 'success',
                message: toastDialog,
              })
            }
            if (action == 'draft') {
              router.back()
            } else {
              router.replace(
                `${PENGELOLAAN_KINERJA_GURU_PATH}/pelaksanaan/beranda`
              )
            }
          },
          onError: (err: CustomError) => {
            setDialogOpen(null)

            const message = `Gagal ${
              action == 'draft' ? 'simpan draf' : 'kumpulkan'
            } dokumen. Coba lagi`
            const errorCode = getErrorCode(err)

            toast({
              message: generateErrorMessage(message, errorCode),
            })
          },
        }
      )
    },
    [router, submitDocumentMutation, toast]
  )

  const onSubmitDraft = useCallback(() => {
    trackEvent({
      activity_type: 'pelaksanaan_click_form_a_konfirmasi_simpan_draf',
      screen_name: CURRENT_SCREEN_NAME,
      event_trigger: CURRENT_SCREEN_NAME,
    })

    const values = getValues()

    handleSaveDocument(values, 'draft')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues, handleSaveDocument])

  const onSubmitDocument = useCallback(
    (formData) => {
      trackEvent({
        activity_type: 'pelaksanaan_click_form_a_konfirmasi_kumpulkan',
        screen_name: CURRENT_SCREEN_NAME,
        event_trigger: CURRENT_SCREEN_NAME,
      })
      handleSaveDocument(formData, 'submit')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSaveDocument]
  )

  const isSubmitted = documentData?.data?.state === 'SUBMITTED'
  const isSaveDraftDisabled =
    !formState.isDirty ||
    !!formState.errors?.observationDate ||
    isUploading ||
    submitDocumentMutation.isLoading ||
    isSubmitted ||
    isEvaluated
  const isSubmitDisabled =
    !formState.isValid ||
    isUploading ||
    submitDocumentMutation.isLoading ||
    isSubmitted ||
    isEvaluated

  const handleBackSave = useCallback(() => {
    if (isSaveDraftDisabled) return true
    const values = getValues()
    handleSaveDocument(values, 'back')
    return true
  }, [getValues, handleSaveDocument, isSaveDraftDisabled])

  useBeforePopState(router, handleBackSave)

  return (
    <div className="pt-14 px-8 pb-8">
      <div className="mx-auto max-w-6xl">
        <Header
          title={
            PENGELOLAAN_KINERJA_PELAKSANAAN_STEPS['DOCUMENT_PREPARATION'].title
          }
          onBack={() => {
            trackEvent({
              activity_type: 'pelaksanaan_click_form_a_back_to_hub',
              screen_name: CURRENT_SCREEN_NAME,
              event_trigger: 'pelaksanaan_hub',
            })
            if (isSaveDraftDisabled) return router.back()
            setDialogOpen('save-draft')
          }}
        />
        {/* content start from here */}
        <div className="max-w-5xl m-auto">
          <div className="flex flex-col space-y-3 mt-9 bg-white p-6 border rounded border-subdued">
            <Text variant="heading-sm" className="!text-black" as="h2">
              Langkah-langkah Persiapan Observasi Praktik Kinerja
            </Text>
            <div className="flex w-full gap-3.5">
              <ObservationStep
                title="Diskusi dengan atasan"
                description="Agar lebih mudah isi formulir."
              />
              <ObservationStep
                title="Bersiap untuk observasi"
                description="Isi Formulir Diskusi Persiapan."
                withIcon
              />
              <ObservationStep
                title="Observasi berlangsung"
                description="Semangat dan semoga lancar!"
                withIcon
              />
            </div>
          </div>
          {/* header */}
          <div className="flex flex-row justify-between items-center mt-9 bg-blue-5 p-6 border-t border-x rounded-t border-subdued">
            <Text variant="heading-lg" as="h2">
              Formulir Diskusi Persiapan
            </Text>
            <button
              className="flex flex-row gap-1 items-center"
              aria-haspopup={true}
              aria-expanded={dialogOpen === 'tujuan'}
              onClick={() => {
                trackEvent({
                  activity_type: 'pelaksanaan_click_form_a_tujuan_formulir',
                  screen_name: CURRENT_SCREEN_NAME,
                  event_trigger: 'pelaksanaan_hub',
                })
                setDialogOpen('tujuan')
              }}
            >
              <Icon fontSize="sm" color="informational">
                help
              </Icon>
              <Text variant="body-sm-bold" className="!text-blue-70">
                Apa tujuan formulir ini?
              </Text>
            </button>
          </div>
          {/* content */}
          <div className="flex flex-col bg-white border-x border-b rounded-b border-subdued">
            {/* first section */}
            <div className="p-6">
              <Text variant="heading-sm" className="mb-2">
                Berdasarkan perencanaan Anda:
              </Text>
              <IndicatorCard
                subIndicatorName={data?.data?.subIndicatorName}
                subIndicatorDefinition={data?.data?.subIndicatorDefinition}
                qualityTarget={data?.data?.qualityTarget}
                contentId={data?.data?.contentId}
                isLoading={isLoadingPelaksanaanProgress}
                onRubrikDialogOpened={() => {
                  trackEvent({
                    activity_type:
                      'pelaksanaan_click_form_a_cek_rubrik_observasi',
                    screen_name: CURRENT_SCREEN_NAME,
                    event_trigger: 'pelaksanaan_hub',
                  })
                }}
                onRubrikDialogClosed={() => {
                  trackEvent({
                    activity_type:
                      'pelaksanaan_click_form_a_mengerti_rubrik_observasi',
                    screen_name: CURRENT_SCREEN_NAME,
                    event_trigger: 'form_a',
                  })
                }}
              />
              <Text variant="action" className="mb-2 mt-12">
                Apa perilaku spesifik yang ingin Anda pelajari dan tampilkan
                pada Observasi Praktik Kinerja?
              </Text>
              <Text color="subdued" className="mb-1">
                Target Perilaku (berdasarkan indikator pilihan Anda)
              </Text>
              <Controller
                control={control}
                name="behavioralTargets"
                rules={{
                  required: 'Wajib dipilih',
                }}
                render={({ field }) => (
                  <CheckboxGroup
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    className="flex flex-row gap-4"
                  >
                    {behaviorLoading && (
                      <>
                        <Shimmer width={260} height={120} />
                        <Shimmer width={260} height={120} />
                        <Shimmer width={260} height={120} />
                      </>
                    )}
                    {behaviorData?.data?.map((thisBehavior, index) => (
                      <Checkbox
                        key={`behavior_${index}`}
                        name={field.name}
                        value={thisBehavior.id}
                        labelPosition="start"
                        className="p-3 border rounded border-subdued w-72 max-w-full"
                        disabled={isSubmitted || isEvaluated}
                      >
                        {thisBehavior.behavioralTarget}
                      </Checkbox>
                    ))}
                  </CheckboxGroup>
                )}
              />
              <div className="mt-12 space-y-2 w-8/12 max-w-full">
                <Text variant="action">
                  Apa saja upaya yang akan Anda lakukan untuk mempelajari Target
                  Perilaku?
                </Text>
                <div className="space-y-1">
                  <Text color="subdued" as="label" htmlFor="upaya-mempelajari">
                    Upaya Mempelajari
                  </Text>
                  <Textarea
                    isDisabled={isSubmitted || isEvaluated}
                    maxLength={250}
                    rows={3}
                    placeholder="Ketik di sini"
                    id="upaya-mempelajari"
                    {...register('behavioralTargetEffort', {
                      required: true,
                      maxLength: 250,
                    })}
                  />
                </div>
              </div>
            </div>
            {/* second section */}
            <SubHeader title="Jadwal Observasi Praktik Kinerja" />
            <div className="p-6 space-y-4">
              <Text>Diskusikan dulu dengan atasan, ya.</Text>
              <div className="w-72 max-w-full space-y-1">
                <Text color="disabled" as="label" htmlFor="tanggal-observasi">
                  Tanggal
                </Text>
                <Controller
                  control={control}
                  name="observationDate"
                  rules={{
                    validate: (value) => {
                      const selectedDate = new Date(value)
                      if (selectedDate.getFullYear() < MIN_YEAR) {
                        return 'Tahun yang dipilih minimal 2024.'
                      }
                      return true
                    },
                  }}
                  render={({ field, fieldState }) => {
                    const errorMessage = fieldState?.error?.message
                    return (
                      <>
                        <DatePicker
                          aria-describedby="tanggal-observasi-error"
                          useCalendar
                          value={field.value}
                          minYear={MIN_YEAR}
                          minValue={`${MIN_YEAR}-01-01`}
                          isDisabled={isSubmitted || isEvaluated}
                          onChange={field.onChange}
                        />
                        {errorMessage && !isSubmitted && !isEvaluated && (
                          <Text
                            variant="helper"
                            color="critical"
                            id="tanggal-observasi-error"
                          >
                            {errorMessage}
                          </Text>
                        )}
                      </>
                    )
                  }}
                />
              </div>
              <div className="max-w-4xl flex flex-row gap-6">
                <div className="w-56 max-w-full">
                  <Text
                    color="disabled"
                    className="mb-1"
                    as="label"
                    htmlFor="jenjang"
                  >
                    Jenjang
                  </Text>
                  <Controller
                    name="observationLevel"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        className="!opacity-100"
                        id="jenjang"
                        placeholder="Pilih di sini"
                        value={field.value}
                        isDisabled={isSubmitted || isEvaluated}
                        onChange={(e) => {
                          setJenjang(e.target.value)
                          field.onChange(e)
                          setFase(jenjangKelasMapping[e.target.value].type)
                          if (e.target.value === 'PAUD') {
                            setKelas(e.target.value)
                            setValue('observationClass', e.target.value)
                            setValue('observationSubject', e.target.value)
                          } else {
                            setKelas('')
                            resetField('observationClass', { defaultValue: '' })
                            resetField('observationSubject', {
                              defaultValue: '',
                            })
                          }
                        }}
                      >
                        <option selected disabled />
                        {Object.keys(jenjangKelasMapping).map(
                          (thisJenjang, index) => (
                            <option
                              value={thisJenjang}
                              key={`jenjang_${index}`}
                            >
                              {thisJenjang}
                            </option>
                          )
                        )}
                      </Select>
                    )}
                  />
                </div>
                <div className="w-56 max-w-full">
                  <Text
                    color="disabled"
                    className="mb-1"
                    as="label"
                    htmlFor="kelas"
                  >
                    Kelas
                  </Text>
                  <Controller
                    name="observationClass"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        placeholder="Pilih di sini"
                        disabled={
                          !jenjang ||
                          !jenjangKelasMapping[jenjang].kelas.length ||
                          isSubmitted ||
                          isEvaluated
                        }
                        id="kelas"
                        value={field.value}
                        onChange={(e) => {
                          setKelas(e.target.value)
                          field.onChange(e)
                        }}
                      >
                        {jenjang &&
                          jenjangKelasMapping[jenjang].kelas.map(
                            (thisClass, index) => (
                              <option value={thisClass} key={`kelas_${index}`}>
                                {thisClass}
                              </option>
                            )
                          )}
                        <option selected disabled />
                      </Select>
                    )}
                  />
                </div>
                <div className="w-80 !max-w-full">
                  <Text
                    color="disabled"
                    className="mb-1"
                    as="label"
                    htmlFor="mapel"
                  >
                    Mata Pelajaran (pilih 1)
                  </Text>
                  <Controller
                    name="observationSubject"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => {
                      return (
                        <Autocomplete.SingleSelect
                          id="mapel"
                          loadingMessage="Mohon tunggu"
                          notFoundMessage="Tidak ditemukan. Cek ejaan dan coba lagi atau ketik “Lainnya” jika memang belum tersedia."
                          isDisabled={
                            !isSuccessMapel ||
                            !jenjang ||
                            !kelas ||
                            isSubmitted ||
                            isEvaluated
                          }
                          placeholder="Ketik di sini"
                          options={mapelList}
                          onChange={(e) => field.onChange(e.value)}
                          value={{
                            value: field.value,
                            label: field.value,
                          }}
                          label="mata pelajaran"
                          defaultValue={{ value: '', label: '' }}
                        />
                      )
                    }}
                  />
                </div>
              </div>
              <div className="w-7/12 max-w-full space-y-1">
                <Text color="disabled" as="label" htmlFor="catatan">
                  Catatan lain (Opsional)
                </Text>
                <Textarea
                  maxLength={250}
                  rows={3}
                  id="catatan"
                  isDisabled={isSubmitted || isEvaluated}
                  placeholder="Contoh: Jam observasi. Anda juga dapat menuliskan catatan lain jika ada."
                  {...register('observationNote', { maxLength: 250 })}
                />
              </div>
            </div>
            {/* TODO: third section */}
            {documentStatus === 'OLD_DOCUMENT_FLOW' && (
              <>
                <SubHeader title="Unggah dokumen (Opsional)" />
                <div className="p-6">
                  <Text className="mb-6">
                    Gunakan dokumen yang sudah tersedia dulu saja untuk Kegiatan
                    Belajar Mengajar (KBM) Anda.
                  </Text>
                  <FileUpload
                    file={fileModulAjar}
                    isDisabled={isEvaluated || isSubmitted}
                    description="Dokumen RPP/Modul Ajar/Perangkat Asesmen"
                    realizationState={
                      isSubmitted || isEvaluated
                        ? 'WAITING_EVALUATION'
                        : undefined
                    }
                    onChange={(file) => {
                      if (file instanceof File) {
                        handleUpload(file)
                      } else {
                        handleBuktiKarya(file)
                      }
                    }}
                    isUploading={isUploading}
                    onError={(error) => toast({ message: error })}
                  />
                  <Text>
                    Format PDF maks. 10 MB. Cek{' '}
                    <Link
                      href={PANDUAN_PENGELOLAAN_GURU_UNGGAH_BUKTI_DUKUNG_URL}
                      target="_blank"
                      className="!p-0"
                    >
                      Panduan
                    </Link>
                    .
                  </Text>
                </div>
              </>
            )}
            {/* footer */}
            {!isEvaluated && (
              <div className="p-6 flex flex-row space-x-2.5 justify-end border-t border-subdued">
                <Button
                  color="white"
                  size="lg"
                  onClick={() => {
                    trackEvent({
                      activity_type: 'pelaksanaan_click_form_a_simpan_draf',
                      screen_name: CURRENT_SCREEN_NAME,
                      event_trigger: CURRENT_SCREEN_NAME,
                    })
                    setDialogOpen('save-draft')
                  }}
                  disabled={isSaveDraftDisabled}
                  isLoading={isUploading}
                >
                  Simpan draf
                </Button>
                <Button
                  size="lg"
                  type="submit"
                  disabled={isSubmitDisabled}
                  isLoading={isUploading}
                  onClick={() => {
                    trackEvent({
                      activity_type: 'pelaksanaan_click_form_a_kumpulkan',
                      screen_name: CURRENT_SCREEN_NAME,
                      event_trigger: CURRENT_SCREEN_NAME,
                    })
                    setDialogOpen('submit')
                  }}
                >
                  Kumpulkan
                </Button>
              </div>
            )}
          </div>
        </div>
        <SaveToDraftDialog
          isLoading={submitDocumentMutation.isLoading}
          isOpen={dialogOpen == 'save-draft'}
          onConfirm={onSubmitDraft}
          onCancel={() => setDialogOpen(null)}
        />
        <SubmitConfirmationDialog
          isLoading={submitDocumentMutation.isLoading}
          isOpen={dialogOpen == 'submit'}
          onCancel={() => {
            trackEvent({
              activity_type: 'pelaksanaan_click_form_a_cek_ulang',
              screen_name: CURRENT_SCREEN_NAME,
              event_trigger: CURRENT_SCREEN_NAME,
            })
            setDialogOpen(null)
          }}
          onConfirm={handleSubmit(onSubmitDocument)}
        />
        <TujuanDialog
          title="Formulir Diskusi Persiapan"
          suggestion="Diskusi dengan atasan agar tujuan formulir ini tercapai."
          isOpen={dialogOpen == 'tujuan'}
          onClose={() => setDialogOpen(null)}
          onConfirm={() => {
            trackEvent({
              activity_type:
                'pelaksanaan_click_form_a_mengerti_tujuan_formulir',
              screen_name: CURRENT_SCREEN_NAME,
              event_trigger: 'form_a',
            })
          }}
          content={[
            {
              title: 'Menyepakati target perilaku',
              description:
                'Guru dan atasan menyepakati target perilaku yang akan diobservasi dan upaya belajar untuk menampilkan perilaku itu secara efektif.',
            },
            {
              title: 'Menyepakati jadwal',
              description:
                'Guru dan atasan menyepakati jadwal Observasi Praktik Kinerja beserta kelengkapan yang dibutuhkan dalam melakukan Observasi Praktik Kinerja.',
            },
          ]}
        />
      </div>
    </div>
  )
}

export default DokumenPersiapan
